import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { AccountBalance as AccountIcon, Add as AddIcon, ArrowBack as BackIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useVenue } from '../../../../context/VenueContext';
import ownershipAccountService from '../../../../services/ownershipAccountService';
import type { OwnershipAccount, OwnershipAccountType } from '../../../../types/venue';

const TYPE_COLORS: Record<OwnershipAccountType, 'primary' | 'warning' | 'default'> = {
  VENUE_OWNED:     'primary',
  CONSIGNED:       'warning',
  EXTERNAL_ENTITY: 'default',
};

const TYPE_LABELS: Record<OwnershipAccountType, string> = {
  VENUE_OWNED:     'Venue Owned',
  CONSIGNED:       'Consigned',
  EXTERNAL_ENTITY: 'External Entity',
};

const colStyle = {
  fontWeight: 600,
  fontSize: '0.72rem',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.04em',
};

const OwnershipAccountList: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { venueId } = useVenue();

  const [accounts, setAccounts] = useState<OwnershipAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!venueId) return;
    setLoading(true);
    ownershipAccountService.listAccounts(venueId)
      .then(res => setAccounts(res.data))
      .catch((err: any) => setError(err?.response?.data?.message ?? 'Failed to load ownership accounts.'))
      .finally(() => setLoading(false));
  }, [venueId]);

  const basePath = `/admin/venues/${venueId}/ownership-accounts`;

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <AccountIcon sx={{ color: theme.palette.primary.main }} />
          <Typography variant="h6" fontWeight={600}>Ownership Accounts</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate(`/admin/venues/${venueId}/inventory`)}
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            Back to Inventory
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate(`${basePath}/create`)}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            New Account
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Name', 'Type', 'Default', 'Status', ''].map(h => (
                    <TableCell key={h} sx={{ ...colStyle, color: theme.palette.text.secondary }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {accounts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                      No ownership accounts yet
                    </TableCell>
                  </TableRow>
                ) : (
                  accounts.map(acc => (
                    <TableRow
                      key={acc._id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`${basePath}/edit/${acc._id}`)}
                    >
                      <TableCell sx={{ fontWeight: 500 }}>{acc.name}</TableCell>
                      <TableCell>
                        <Chip
                          label={TYPE_LABELS[acc.type]}
                          color={TYPE_COLORS[acc.type]}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {acc.isVenueDefault && (
                          <Chip label="Default" color="success" size="small" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={acc.active ? 'Active' : 'Inactive'}
                          color={acc.active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          onClick={e => { e.stopPropagation(); navigate(`${basePath}/edit/${acc._id}`); }}
                          sx={{ textTransform: 'none' }}
                        >
                          Edit
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
};

export default OwnershipAccountList;
